import { Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";

interface LongTextProps {
  text: string;
  className?: string;
  maxWidth?: string | number;
  maxFontSize?: number;
  maxLines?: number;
}

export const LongText: React.FC<LongTextProps> = ({
  text,
  className = "",
  maxWidth = "100%",
  maxFontSize,
  maxLines,
}) => {
  const style: React.CSSProperties = {
    maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
    overflow: "hidden",
    textOverflow: "ellipsis",
    ...(maxLines
      ? {
          display: "-webkit-box",
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal",
        }
      : {
          display: "inline-block",
          whiteSpace: "nowrap",
        }),
  };

  const displayText = maxFontSize && text.length > maxFontSize ? `${text.substring(0, maxFontSize)}...` : text;

  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = spanRef.current;
      if (element) {
        if (maxLines) {
          const lineHeight = parseInt(getComputedStyle(element).lineHeight);
          const maxHeight = lineHeight * maxLines;
          setIsTruncated(element.scrollHeight > maxHeight || displayText !== text);
        } else {
          setIsTruncated(element.scrollWidth > element.clientWidth || displayText !== text);
        }
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text, displayText, maxLines]);

  return (
    <>
      {isTruncated ? (
        <Tooltip placement="top" title={text}>
          <span className={className} ref={spanRef} style={style}>
            {displayText}
          </span>
        </Tooltip>
      ) : (
        <span className={className} ref={spanRef} style={style}>
          {displayText}
        </span>
      )}
    </>
  );
};
